import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchForm, submitForm } from '@/store/slices/formSlice';
import MultiColumnForm from '@/components/forms/MultiColumnForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { fetchUserSubmissions } from '../store/slices/formSlice';

const FormDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentForm, loading, error } = useSelector(state => state.forms);
  const { user } = useSelector(state => state.auth);
  const [userSubmissions, setUserSubmissions] = useState([]);

 useEffect(() => {
    if (id) {
      dispatch(fetchForm(id));
      dispatch(fetchUserSubmissions(id)); // Use the Redux action instead
    }
  }, [id, dispatch]);



  const handleSubmit = (formData) => {
    dispatch(submitForm({
      formId: id,
      data: formData
    }))
      .unwrap()
      .then(() => {
        navigate('/forms', {
          state: { message: 'Form submitted successfully!' }
        })
      })
      .catch((error) => {
        console.error('Form submission error:', error)
      })
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      case 'submitted':
      case 'under_review':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" /> Under Review</Badge>;
      case 'needs_correction':
        return <Badge variant="outline" className="border-amber-500 text-amber-500">Needs Correction</Badge>;
      default:
        return <Badge variant="outline">Draft</Badge>;
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8">Loading form...</div>;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive">Error</h2>
          <p className="text-muted-foreground">{error}</p>
          <Button onClick={() => navigate('/forms')} className="mt-4">
            Back to Forms
          </Button>
        </div>
      </div>
    );
  }

  if (!currentForm) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Form not found</h2>
          <Button onClick={() => navigate('/forms')} className="mt-4">
            Back to Forms
          </Button>
        </div>
      </div>
    );
  }

  // Check if user has any approved submission for any form
  const hasApprovedSubmission = userSubmissions.some(sub => sub.status === 'approved');
  const currentFormSubmission = userSubmissions.find(sub => sub.form_id === parseInt(id));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/forms')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-3xl font-bold">{currentForm.name}</h1>
      </div>

      {hasApprovedSubmission && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Your application has been approved! You cannot submit additional forms.
          </AlertDescription>
        </Alert>
      )}

      {currentFormSubmission && (
        <Alert variant={currentFormSubmission.status === 'rejected' ? 'destructive' : 'default'}>
          {currentFormSubmission.status === 'rejected' ? (
            <XCircle className="h-4 w-4" />
          ) : currentFormSubmission.status === 'approved' ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <Clock className="h-4 w-4" />
          )}
          <AlertDescription>
            You have already submitted this form. Status: {getStatusBadge(currentFormSubmission.status)}
            {currentFormSubmission.review_notes && (
              <div className="mt-2">
                <strong>Review Notes:</strong> {currentFormSubmission.review_notes}
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Form Details</CardTitle>
          <CardDescription>{currentForm.description}</CardDescription>
        </CardHeader>
        <CardContent>
          {hasApprovedSubmission ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <p>Your application has been approved. You cannot submit additional forms.</p>
            </div>
          ) : currentFormSubmission && !currentFormSubmission.canBeEdited ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4" />
              <p>Your submission is being processed. Please wait for the decision.</p>
            </div>
          ) : (
            <MultiColumnForm
              form={currentForm}
              onSubmit={handleSubmit}
              user={user}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FormDetail;